import { type SxProps, type Theme } from '@mui/material';
import { LayoutTypes } from './types';

const getMainStyles = (isListFirst?: boolean) =>
  ({
    display: 'grid',
    flex: 1,
    overflowY: 'auto',
    '& > *': (t) => ({
      display: 'flex',
      flexDirection: 'column !important',
      gap: 30,
      overflowY: 'auto',
      overflowX: 'hidden',
      [t.breakpoints.down('lg')]: {
        gap: 18,
      },
      [t.breakpoints.up('xs')]: isListFirst
        ? {
            '&:not(:last-child)': {
              borderRight: '1px solid',
              borderColor: t.palette.border.default,
            },
          }
        : {},
      [t.breakpoints.up('lg')]: isListFirst
        ? {}
        : {
            '&:not(:last-child)': {
              borderRight: '1px solid',
              borderColor: t.palette.border.default,
            },
          },
      ...(isListFirst
        ? {
            '&:not(:first-child)': {
              p: 24,
            },
          }
        : {
            p: 24,
          }),
    }),
  }) satisfies SxProps<Theme>;

const tabletTwoColumns = {
  gridTemplateColumns: '246px 1fr',
  '& > *:last-child': {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    p: 24,
    overflow: 'auto',
  },
} satisfies SxProps<Theme>;

const oneColumn = {
  display: 'flex',
  flexDirection: 'column',
  overflow: 'auto',
  '& > *': (t) => ({
    [t.breakpoints.only('sm')]: {
      p: 24,
      pb: 0,
      gap: 18,
      display: 'flex',
      flexDirection: 'column !important',
    },
    [t.breakpoints.only('xs')]: {
      p: 16,
      pb: 0,
      gap: 18,
      display: 'flex',
      flexDirection: 'column !important',
    },
  }),
} satisfies SxProps<Theme>;

export const stylesByCondition = {
  [LayoutTypes.DI]: (t) => ({
    ...getMainStyles(),
    [t.breakpoints.up('xxl')]: {
      gridTemplateColumns: '450px 1fr',
    },
    [t.breakpoints.only('xl')]: {
      gridTemplateColumns: '450px 1fr',
    },
    [t.breakpoints.only('lg')]: {
      gridTemplateColumns: '384px 1fr',
    },
    [t.breakpoints.only('sm')]: {
      ...oneColumn,
    },
    [t.breakpoints.only('xs')]: {
      ...oneColumn,
    },
  }),
  [LayoutTypes.DL_DI]: (t) => ({
    ...getMainStyles(true),
    [t.breakpoints.up('xxl')]: {
      gridTemplateColumns: '450px 474px 1fr',
    },
    [t.breakpoints.only('xl')]: {
      gridTemplateColumns:
        'minmax(423px, 450px) minmax(423px, 474px) minmax(642px, 1fr)',
    },
    [t.breakpoints.only('lg')]: {
      gridTemplateColumns: '384px 1fr',
      '& > *:last-child': {
        display: 'flex',
        flex: 1,
        p: 24,
      },
    },
    [t.breakpoints.only('sm')]: {
      ...tabletTwoColumns,
    },
    [t.breakpoints.only('xs')]: {
      ...oneColumn,
    },
  }),
  [LayoutTypes.DB]: (t) => ({
    ...getMainStyles(),
    [t.breakpoints.up('xxl')]: {
      gridTemplateColumns: '450px 1fr',
    },
    [t.breakpoints.only('xl')]: {
      gridTemplateColumns: '450px 1fr',
    },
    [t.breakpoints.only('lg')]: {
      gridTemplateColumns: '384px 1fr',
    },
    [t.breakpoints.only('sm')]: {
      ...oneColumn,
    },
    [t.breakpoints.only('xs')]: {
      ...oneColumn,
    },
  }),
  [LayoutTypes.DL_DB]: (t) => ({
    ...getMainStyles(true),
    [t.breakpoints.up('xxl')]: {
      gridTemplateColumns: '450px 1fr',
    },
    [t.breakpoints.only('xl')]: {
      gridTemplateColumns: '450px 1fr',
    },
    [t.breakpoints.only('lg')]: {
      gridTemplateColumns: '384px 1fr',
    },
    [t.breakpoints.only('sm')]: {
      ...tabletTwoColumns,
    },
    [t.breakpoints.only('xs')]: {
      ...oneColumn,
    },
  }),
  [LayoutTypes.DBI]: (t) => ({
    [t.breakpoints.up('xxl')]: {
      ...getMainStyles(),
      gridTemplateColumns: '450px 790px 1fr',
    },
    [t.breakpoints.only('xl')]: {
      ...getMainStyles(),
      gridTemplateColumns: '360px minmax(564px, 1fr) minmax(564px, 1fr)',
    },
    [t.breakpoints.only('lg')]: {
      display: 'flex',
      flexDirection: 'column',
      '& > *:first-child': {
        p: 24,
        borderBottom: '1px solid',
        borderColor: (theme) => theme.palette.border.default,
      },
      '& > *:last-child': {
        display: 'flex',
        flex: 1,
        '& > *:first-child': {
          p: 24,
          borderRight: '1px solid',
          borderColor: (theme) => theme.palette.border.default,
        },
        '& > *': {
          p: 24,
        },
      },
    },
    [t.breakpoints.only('sm')]: {
      ...oneColumn,
    },
    [t.breakpoints.only('xs')]: {
      ...oneColumn,
    },
  }),
  [LayoutTypes.DL_DBI]: (t) => ({
    ...getMainStyles(true),
    [t.breakpoints.up('xxl')]: {
      gridTemplateColumns: '450px minmax(790px, 1fr) minmax(632px, 1fr)',
    },
    [t.breakpoints.only('xl')]: {
      gridTemplateColumns: '252px minmax(604px, 1fr) minmax(632px, 1fr)',
    },
    [t.breakpoints.only('lg')]: {
      gridTemplateColumns: '252px 1fr',
    },
    [t.breakpoints.only('sm')]: {
      ...tabletTwoColumns,
    },
    [t.breakpoints.only('xs')]: {
      ...oneColumn,
    },
  }),
} satisfies Record<LayoutTypes, SxProps<Theme>>;
